import { useCallback, useEffect, useState } from 'react';
import { getCorpusDnaOverlay, type DnaOverlay } from './api';

/**
 * The Refine tab's real-threads data hook — the Throughline analogue of apps/
 * web/src/lib/useDnaOverlay.ts, scoped to what this tab needs: a read-only
 * fetch of the woven overlay (through-line, drivers, spine, era-to-era
 * threads). Own loading + error state so a DNA-overlay failure NEVER blocks
 * the rest of Refine from rendering — the voice hero and the corpus canvas
 * both stand on their own regardless of this hook's outcome.
 *
 * Deliberately narrower than apps/web's hook: this tab has no probe/voice-turn
 * surface that mutates the overlay mid-session yet (apps/web's applyOverlay
 * exists to fold a probe reply's fresh overlay in without a round-trip — out
 * of this brief's scope), so this hook is read + refresh only.
 */
export interface DnaOverlayState {
  overlay: DnaOverlay | null;
  loading: boolean;
  error: string | null;
}

export function useDnaOverlay() {
  const [state, setState] = useState<DnaOverlayState>({
    overlay: null,
    loading: true,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const overlay = await getCorpusDnaOverlay();
      setState({ overlay, loading: false, error: null });
    } catch (err) {
      setState({
        overlay: null,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { state, refresh };
}
